import type { ReactNode } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { LibraryGalleryStatus, type LibraryGalleryError } from './LibraryGalleryStatus';
import { LibraryGalleryFooter } from './LibraryGalleryFooter';

export interface LibraryGalleryProps {
  ariaLabel: string;
  /** The tiles — `<li>` elements, each wrapping a `LibraryCard`. */
  children: ReactNode;
  itemCount: number;
  totalCount: number;
  isLoading?: boolean;
  error?: LibraryGalleryError | null;
  emptyState: ReactNode;
  onLoadMore: () => void;
  isLoadingMore?: boolean;
  /**
   * The grid still shows the previous result while a new filter, a retry or a background refetch is in
   * flight. Marks the list busy, dims the stale tiles and says so out loud and on screen.
   */
  isRefreshing?: boolean;
  /**
   * Fetching the next page failed while earlier pages are still loaded. The grid stays on screen and the
   * footer shows this message as an inline alert whose "Try again" calls `onLoadMore`. Use `error` instead
   * only when there is nothing to show (the first page failed).
   */
  loadMoreError?: string | null;
  testIdPrefix: string;
  /** Noun for the count line — "blocks" / "templates". */
  noun: string;
  /** Singular of `noun` for a count of exactly one ("Showing 1 of 1 template"). Defaults to `noun` without a trailing "s". */
  singularNoun?: string;
}

/**
 * The shared library gallery shell: one set of peer tiles on the grey page, common to the
 * Block Library, the Templates Library and the federated pickers built on this kit. Loading /
 * empty / error states are mutually exclusive (`LibraryGalleryStatus`); the grid and its
 * "Load more" footer (`LibraryGalleryFooter`) render only once none of them apply.
 */
export function LibraryGallery({
  ariaLabel,
  children,
  itemCount,
  totalCount,
  isLoading = false,
  error = null,
  emptyState,
  onLoadMore,
  isLoadingMore = false,
  isRefreshing = false,
  loadMoreError = null,
  testIdPrefix,
  noun,
  singularNoun
}: LibraryGalleryProps) {
  const isEmpty = !isLoading && !error && itemCount === 0;
  const showGrid = !isLoading && !error && itemCount > 0;
  const hasMore = showGrid && itemCount < totalCount;

  return (
    <Box data-testid={`${testIdPrefix}:gallery`}>
      <LibraryGalleryStatus
        isLoading={isLoading}
        error={error}
        isEmpty={isEmpty}
        emptyState={emptyState}
        loadingLabel={`Loading ${noun}…`}
        testIdPrefix={testIdPrefix}
      />

      {showGrid && isRefreshing ? (
        <Box
          role="status"
          aria-live="polite"
          data-testid={`${testIdPrefix}:refreshing`}
          sx={{ display: 'flex', alignItems: 'center', gap: 'var(--size-8)', mb: 'var(--size-12)', color: 'var(--text-caption)' }}
        >
          <CircularProgress size="var(--size-16)" aria-hidden />
          <Typography variant="body2" component="span">
            Updating {noun}…
          </Typography>
        </Box>
      ) : null}

      {showGrid ? (
        <Box
          component="ul"
          aria-label={ariaLabel}
          aria-busy={isRefreshing || undefined}
          sx={{
            opacity: isRefreshing ? 0.6 : 1,
            transition: 'opacity var(--duration-fast, 150ms) ease',
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'grid',
            // Cap at four columns (Figma bl.library.default / tl.library.default). auto-fill at 220px
            // fits a fifth column once the content area passes ~1160px.
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, max(220px, calc((100% - 48px) / 4))), 1fr))',
            minWidth: 0,
            columnGap: 'var(--size-16)',
            rowGap: 'var(--space-layout-sm, var(--size-16))',
            alignItems: 'stretch'
          }}
        >
          {children}
        </Box>
      ) : null}

      {showGrid ? (
        <LibraryGalleryFooter
          itemCount={itemCount}
          totalCount={totalCount}
          noun={noun}
          singularNoun={singularNoun}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          isLoadingMore={isLoadingMore}
          isRefreshing={isRefreshing}
          loadMoreError={loadMoreError}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
    </Box>
  );
}
